// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — GraceAura.jsx   ·   THE KING'S GRACE, AS LIGHT  (v1.0.0)
// ────────────────────────────────────────────────────────────────────
// The pictures for kingsGrace.js. One RECIPE per Grace, drawn by two
// renderers that read the SAME recipe:
//
//   <GraceAura2D graceId layer="back"|"front" />   DOM, fills its parent
//       box (a companion sprite's square). Render "back" before the
//       sprite and "front" after it; the caller orders them.
//   <GraceAura3D graceId height radius />          react-three-fiber, a
//       group standing at the body's feet (y = 0), wrapping `height`.
//
// ⭐ ONE IDEA, TWO SCALES. A renderer is never told what a Grace looks
// like — it is handed layers ("floor", "sheath", "motes", "orbit" …)
// with numbers. So the menu picture and the plaza picture cannot drift
// into two different finishes; they would have to disagree about a
// number in the table below, in one file.
//
// ⭐ LOCALIZED BY LAW. caP'n: "we don't want it over the top especially
// some games will be more cramped and that Grace could easily start
// bugging the screen, definitely want it pretty localized to the
// model." Every layer lives inside 0.75 × height of the body's axis
// and 1.2 × height vertically. Nothing reaches the screen edge.
//
// ── THE TRAPS THIS FILE OBEYS ───────────────────────────────────────
//   · Keyframes go into document.head IMPERATIVELY with a self-healing
//     guard (compare textContent, replace on drift) — never a JSX
//     <style>, which dies with the first unmount (the J'ynxi freeze).
//   · No z-index on the 2D root. The caller orders back/front.
//   · Seeded layout (mulberry32), never Math.random() in render.
//   · 3D: ZERO lights added. Every material is MeshBasic, additive or
//     normal, transparent, depthWrite false, toneMapped false. Nothing
//     sets React state from a frame; all motion is refs in useFrame.
//   · pointer-events: none everywhere. A Grace never eats a click.
//   · Depends on react, three and @react-three/fiber only — nothing
//     from the host game — so any title can vendor it unchanged.
// ════════════════════════════════════════════════════════════════════

import React, { useMemo, useRef, useEffect } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GRACES, renderableGrace } from "./kingsGrace";

export const GRACE_AURA_VERSION = "1.0.0";

// ── seeded PRNG ─────────────────────────────────────────────────────
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const hash = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };

// ════════════════════════════════════════════════════════════════════
// THE RECIPES — the only place a Grace's look is written down.
// Units: y is a fraction of body height (0 = feet, 1 = crown of head);
// r is a fraction of body height; speed/period are seconds.
// c: 1 = color, 2 = color2 (lit edge), 3 = color3 (deep).
// ════════════════════════════════════════════════════════════════════
export const GRACE_RECIPES = {
  // TERROR ────────────────────────────────────────────────────────────
  quicksilver: [
    ["sheath", { tex: "bands", flow: "counter", speed: 3.4, op: 0.32 }],
    ["floor",  { op: 0.3, w: 0.05 }],
    ["motes",  { n: 5, shape: "dot", dir: "up", speed: 4.5, size: 0.7, c: 2 }],
  ],
  molten: [
    ["floor",  { op: 0.45, w: 0.12, c: 3 }],
    ["flame",  { n: 7, speed: 0.9, op: 0.85 }],
    ["motes",  { n: 8, shape: "ember", dir: "up", speed: 2.6, size: 0.8, c: 2, twinkle: true }],
  ],
  prism: [
    ["prism",  { speeds: [3.1, 4.3, 5.6], amp: 0.16, op: 0.8 }],
    ["motes",  { n: 4, shape: "star", dir: "up", speed: 4, size: 0.9, rainbow: true }],
  ],
  maelstrom: [
    ["vortex", { speed: 2.4, op: 0.85 }],
    ["sheath", { tex: "smoke", flow: "down", speed: 5, op: 0.45, c: 1 }],
    ["motes",  { n: 8, shape: "dot", dir: "in", speed: 2.2, size: 0.6, c: 2 }],
  ],
  rend: [
    ["fissure", { period: 3.2, op: 1 }],
    ["floor",   { op: 0.35, w: 0.06 }],
    ["motes",   { n: 9, shape: "ember", dir: "up", speed: 2, size: 0.7, c: 1 }],
  ],
  jester: [
    ["orbit",  { n: 4, shape: "diamond", r: 0.46, y: 0.58, speed: 4.5, size: 1, c: 1 }],
    ["orbit",  { n: 4, shape: "diamond", r: 0.38, y: 0.3, speed: -6.5, size: 0.8, c: 2 }],
    ["sheath", { tex: "ripple", flow: "down", speed: 4, op: 0.3 }],
    ["grin",   { period: 5, y: 1.14 }],
  ],
  halo: [
    ["floor", { pulse: true, n: 2, period: 2.6, op: 0.75, w: 0.04, c: 2 }],
    ["halo",  { y: 1.1, r: 0.2, op: 0.85, w: 0.035, c: 2 }],
    ["motes", { n: 6, shape: "star", dir: "still", speed: 2.2, size: 0.8, c: 2, twinkle: true }],
  ],
  caustics: [
    ["sheath", { tex: "caustic", flow: "up", speed: 7, op: 0.55 }],
    ["floor",  { op: 0.35, w: 0.1, breathe: 3.6 }],
    ["motes",  { n: 8, shape: "bubble", dir: "up", speed: 3.5, size: 0.9, c: 2 }],
  ],
  griddle: [
    ["griddle", { period: 5, op: 0.7 }],
    ["motes",   { n: 10, shape: "shred", dir: "rise", speed: 3, size: 1.5, c: 2, twinkle: true }],
    ["floor",   { op: 0.45, w: 0.05, c: 1, breathe: 5 }],
  ],
  grip: [
    ["plates", { n: 4, period: 2.4, op: 0.9 }],
    ["floor",  { op: 0.4, w: 0.05, c: 3 }],
  ],
  // ANNIHILATION ──────────────────────────────────────────────────────
  edict: [
    ["orbit", { n: 5, shape: "rune", r: 0.44, y: 0.46, speed: 9, size: 1.3, c: 1 }],
    ["stamp", { period: 4, op: 0.85 }],
    ["floor", { op: 0.3, w: 0.04, c: 1 }],          // the seal's standing ring, so the Edict reads between stamps
  ],
  whisperleaf: [
    ["motes", { n: 14, shape: "leaf", dir: "spiral", speed: 5, size: 1.7, mix: true }],
    ["floor", { op: 0.22, w: 0.14, c: 3 }],
  ],
  drowned: [
    ["crown", { y: 1.12, op: 0.85 }],
    ["motes", { n: 7, shape: "drop", dir: "down", speed: 1.8, size: 0.8, c: 2 }],
    ["floor", { pulse: true, n: 1, period: 3.2, op: 0.55, w: 0.06, c: 1 }],
  ],
  heresy: [
    ["cracked", { y: 1.06, period: 3.6, op: 0.95 }],
    ["motes",   { n: 7, shape: "ash", dir: "sink", speed: 4.5, size: 0.9, c: 2 }],
  ],
};

// Which layers sit in FRONT of the body in the 2D picture. Everything
// else draws behind. (3D needs no split — depth does it.)
const FRONT_2D = { motes: true, fissure: true, grin: true, plates: true };

export const recipeOf = (graceId) => {
  const id = renderableGrace(graceId);
  if (!id) return null;
  const g = GRACES[id];
  return { grace: g, layers: GRACE_RECIPES[g.motion] || [] };
};
const pick = (g, c) => (c === 2 ? g.color2 : c === 3 ? g.color3 : g.color);
const RAINBOW = ["#ff4d6d", "#ffd23f", "#3ddc97", "#4d9bff", "#c77dff"];

// ════════════════════════════════════════════════════════════════════
// 2D — DOM
// ════════════════════════════════════════════════════════════════════
const KEYS_ID = "ie-grace-aura-keys";
const KEYS = `
@keyframes gaSpin     { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
@keyframes gaSpinRev  { from { transform: rotate(360deg) } to { transform: rotate(0deg) } }
@keyframes gaBandUp   { from { background-position: 0 0% } to { background-position: 0 -200% } }
@keyframes gaBandDown { from { background-position: 0 0% } to { background-position: 0 200% } }
@keyframes gaDrift    { from { background-position: 0% 0% } to { background-position: 0% -200% } }
@keyframes gaDriftC   { from { background-position: 0% 0% } to { background-position: -350% -233.3333% } }
@keyframes gaBreathe  { 0%,100% { opacity: var(--ga-lo, .25); transform: translate(-50%,-50%) scale(.94) } 50% { opacity: var(--ga-hi, .6); transform: translate(-50%,-50%) scale(1.04) } }
@keyframes gaPulse    { 0% { opacity: 0; transform: translate(-50%,-50%) scale(.35) } 18% { opacity: var(--ga-hi, .7) } 100% { opacity: 0; transform: translate(-50%,-50%) scale(1.25) } }
@keyframes gaMote     { 0% { transform: translate(0,0); opacity: 0 } 15% { opacity: 1 } 80% { opacity: 1 } 100% { transform: translate(var(--ga-dx,0%), var(--ga-dy,-30%)); opacity: 0 } }
@keyframes gaTwinkle  { 0%,100% { opacity: .15; transform: scale(.6) } 50% { opacity: 1; transform: scale(1.1) } }
@keyframes gaFlame    { 0% { transform: translate(-50%,0) scaleY(.55) scaleX(1); opacity: 0 } 25% { opacity: var(--ga-hi,.8) } 60% { transform: translate(-50%,-18%) scaleY(1.1) scaleX(.8); opacity: var(--ga-hi,.8) } 100% { transform: translate(-50%,-42%) scaleY(.4) scaleX(.5); opacity: 0 } }
@keyframes gaPrism    { 0%,100% { transform: translate(-50%, calc(-50% + var(--ga-off,0px))) } 50% { transform: translate(-50%, calc(-50% - var(--ga-off,0px))) } }
@keyframes gaRend     { 0%,55%,100% { transform: translate(-50%,-50%) scaleX(.12) scaleY(.85); opacity: .22 } 64% { transform: translate(-50%,-50%) scaleX(.6) scaleY(1); opacity: 1 } 70% { transform: translate(-50%,-50%) scaleX(1.4) scaleY(1.05); opacity: 1; filter: brightness(2.2) } 86% { transform: translate(-50%,-50%) scaleX(.2) scaleY(.9); opacity: .5 } }
@keyframes gaOrbit    { from { transform: rotate(0deg) translateX(var(--ga-r,30cqw)) rotate(0deg) scaleY(2.6) rotate(45deg) } to { transform: rotate(360deg) translateX(var(--ga-r,30cqw)) rotate(-360deg) scaleY(2.6) rotate(45deg) } }
@keyframes gaFrontHalf { 0% { opacity: 1 } 50% { opacity: 0 } 100% { opacity: 0 } }
@keyframes gaBackHalf  { 0% { opacity: 0 } 50% { opacity: 1 } 100% { opacity: 1 } }
@keyframes gaGrin     { 0%,62%,100% { opacity: 0; transform: translate(-50%,-50%) scale(.6) } 70% { opacity: .95; transform: translate(-50%,-50%) scale(1) } 82% { opacity: .95; transform: translate(-50%,-50%) scale(1) } 86% { opacity: .95; transform: translate(-50%,-50%) scale(1, .25) } 90% { opacity: .95; transform: translate(-50%,-50%) scale(1) } }
@keyframes gaGriddle  { 0%,100% { background-color: var(--ga-a) } 45% { background-color: var(--ga-b) } 70% { background-color: var(--ga-c) } }
@keyframes gaGrip     { 0%,100% { transform: rotate(var(--ga-a0)) translateY(calc(-1 * var(--ga-far))) } 40%,60% { transform: rotate(var(--ga-a0)) translateY(calc(-1 * var(--ga-near))) } }
@keyframes gaStamp    { 0%,70%,100% { opacity: 0; transform: translate(-50%,-50%) scale(1.6) } 78% { opacity: var(--ga-hi,.85); transform: translate(-50%,-50%) scale(1) } 92% { opacity: calc(var(--ga-hi,.85) * .5); transform: translate(-50%,-50%) scale(1.02) } }
@keyframes gaBob      { 0%,100% { transform: translate(-50%,-50%) translateY(0) } 50% { transform: translate(-50%,-50%) translateY(-3%) } }
@keyframes gaShard    { 0%,100% { transform: rotate(var(--ga-a0)) translateY(calc(-1 * var(--ga-r))) } 45%,60% { transform: rotate(var(--ga-a0)) translateY(calc(-1 * var(--ga-r) - var(--ga-gap))) } }
@keyframes gaBleed    { 0%,40%,100% { opacity: 0 } 50%,58% { opacity: .9 } }
.ga-still, .ga-still * { animation-play-state: paused !important; }
`;
function ensureKeys() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(KEYS_ID);
  if (el) { if (el.textContent === KEYS) return; el.remove(); }   // SELF-HEAL, not `if (el) return`
  const st = document.createElement("style");
  st.id = KEYS_ID;
  st.textContent = KEYS;
  document.head.appendChild(st);
}

// box geometry (percent of the square the sprite fills)
const FEET = 90;           // % from top
const SPAN = 78;           // % of box that is "one body height"
const yPct = (y) => FEET - y * SPAN;
const abs = { position: "absolute", pointerEvents: "none" };

const MOTE_SHAPES_2D = {
  dot:     (c, s) => ({ width: `${3 * s}cqw`, height: `${3 * s}cqw`, borderRadius: "50%", background: c, boxShadow: `0 0 ${4 * s}cqw ${c}` }),
  ember:   (c, s) => ({ width: `${2.4 * s}cqw`, height: `${3.6 * s}cqw`, borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%", background: c, boxShadow: `0 0 ${5 * s}cqw ${c}` }),
  star:    (c, s) => ({ width: `${5 * s}cqw`, height: `${5 * s}cqw`, background: c, clipPath: "polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%)", filter: `drop-shadow(0 0 ${3 * s}cqw ${c})` }),
  bubble:  (c, s) => ({ width: `${4 * s}cqw`, height: `${4 * s}cqw`, borderRadius: "50%", border: `1px solid ${c}`, background: `radial-gradient(circle at 35% 35%, ${c}aa, transparent 60%)` }),
  shred:   (c, s) => ({ width: `${1.4 * s}cqw`, height: `${5 * s}cqw`, borderRadius: 2, background: c, transform: "rotate(35deg)" }),
  leaf:    (c, s) => ({ width: `${5 * s}cqw`, height: `${3 * s}cqw`, borderRadius: "0 80% 0 80%", background: `linear-gradient(135deg, ${c}, ${c}66)`, boxShadow: `0 0 ${3 * s}cqw ${c}` }),
  drop:    (c, s) => ({ width: `${2.6 * s}cqw`, height: `${4 * s}cqw`, borderRadius: "50% 50% 50% 50% / 30% 30% 70% 70%", background: c, boxShadow: `0 0 ${3 * s}cqw ${c}` }),
  ash:     (c, s) => ({ width: `${3 * s}cqw`, height: `${3 * s}cqw`, background: c, clipPath: "polygon(20% 0,100% 30%,70% 100%,0 70%)", boxShadow: `0 0 ${3 * s}cqw ${c}` }),
};

// Each mote is a FULL-BOX wrapper that travels (so translate % is a
// percentage of the sprite square, not of a 3px dot), carrying one dot
// placed at its start point.
function Motes2D({ g, p, seed }) {
  const rng = mulberry32(seed);
  const out = [];
  for (let i = 0; i < p.n; i++) {
    const c = p.rainbow ? RAINBOW[i % RAINBOW.length]
      : p.mix ? (i % 2 ? g.color2 : g.color) : pick(g, p.c);
    const shape = (MOTE_SHAPES_2D[p.shape] || MOTE_SHAPES_2D.dot)(c, p.size || 1);
    const a = rng() * Math.PI * 2;
    const rx = 16 + rng() * 14;                 // % of box, around the axis
    let x = 50 + Math.cos(a) * rx;
    let y = yPct(0.1 + rng() * 0.75);
    let dx = 0, dy = -28;
    switch (p.dir) {
      case "up":     y = yPct(rng() * 0.3);          dy = -(40 + rng() * 22); dx = (rng() - 0.5) * 8; break;
      case "rise":   y = yPct(0.02 + rng() * 0.08);  dy = -(12 + rng() * 10); dx = (rng() - 0.5) * 16; break;
      case "down":   x = 50 + (rng() - 0.5) * 26; y = yPct(1.04); dy = 70 + rng() * 10; break;
      case "sink":   y = yPct(0.7 + rng() * 0.4);    dy = 26 + rng() * 20; dx = (rng() - 0.5) * 10; break;
      case "in":     x = 50 + Math.cos(a) * 34; y = yPct(0.25 + rng() * 0.5);
                     dx = (50 - x) * 0.8; dy = (yPct(0.04) - y) * 0.8; break;
      case "spiral": y = yPct(rng() * 0.2);          dy = -(58 + rng() * 18); dx = (i % 2 ? 1 : -1) * (12 + rng() * 10); break;
      default: break;                             // "still"
    }
    const still = p.dir === "still";
    const dur = p.speed * (0.75 + rng() * 0.5);
    const delay = -rng() * dur;
    out.push(
      <span key={i} style={{
        ...abs, inset: 0,
        "--ga-dx": `${dx}%`, "--ga-dy": `${dy}%`,
        animation: still ? undefined : `gaMote ${dur}s ${p.dir === "down" ? "ease-in" : "ease-out"} ${delay}s infinite`,
      }}>
        <span style={{
          ...abs, left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)",
        }}>
          <span style={{
            display: "block", ...shape,
            animation: still || p.twinkle
              ? `gaTwinkle ${still ? dur : 0.6 + rng()}s ease-in-out ${still ? delay : 0}s infinite` : undefined,
          }} />
        </span>
      </span>
    );
  }
  return <>{out}</>;
}

// a flat ellipse at the feet (or pulsing rings leaving them)
function Floor2D({ g, p }) {
  const c = pick(g, p.c);
  const base = {
    ...abs, left: "50%", top: `${FEET}%`, width: "70%", height: "17%",
    borderRadius: "50%", transform: "translate(-50%,-50%)",
  };
  if (p.pulse) {
    return <>{Array.from({ length: p.n || 1 }, (_, i) => (
      <span key={i} style={{
        ...base, border: `${Math.max(0.9, p.w * 26)}cqw solid ${c}`, boxShadow: `0 0 5cqw ${c}, inset 0 0 5cqw ${c}`,
        "--ga-hi": p.op, opacity: 0,
        animation: `gaPulse ${p.period}s ease-out ${-(i * p.period) / (p.n || 1)}s infinite`,
      }} />
    ))}</>;
  }
  return <span style={{
    ...base,
    background: `radial-gradient(ellipse, transparent ${Math.round(58 - p.w * 120)}%, ${c} 64%, transparent 72%)`,
    opacity: p.op,
    ...(p.breathe ? { "--ga-lo": p.op * 0.4, "--ga-hi": p.op, animation: `gaBreathe ${p.breathe}s ease-in-out infinite` } : null),
  }} />;
}

// the aura rim hugging the body — a textured ellipse with a hollow core
const SHEATH_BG = {
  bands:   (g) => `repeating-linear-gradient(180deg, transparent 0 22%, ${g.color2}cc 30%, ${g.color} 34%, transparent 44% 50%)`,
  smoke:   (g) => `repeating-radial-gradient(circle at 30% 20%, ${g.color}aa 0 6%, transparent 9% 14%, ${g.color3}cc 16%, transparent 22%)`,
  ripple:  (g) => `repeating-linear-gradient(180deg, transparent 0 10%, ${g.color}99 14%, transparent 18% 25%)`,
  caustic: (g) => `repeating-radial-gradient(circle at 20% 30%, transparent 0 7%, ${g.color2}dd 8%, transparent 10% 13%), repeating-radial-gradient(circle at 75% 60%, transparent 0 6%, ${g.color}cc 7%, transparent 9% 12%)`,
};
function Sheath2D({ g, p }) {
  const mask = "radial-gradient(ellipse 50% 50% at 50% 50%, transparent 38%, #000 62%, #000 72%, transparent 100%)";
  const common = {
    ...abs, left: "50%", top: `${yPct(0.52)}%`, width: "74%", height: "96%",
    transform: "translate(-50%,-50%)", borderRadius: "50%",
    WebkitMaskImage: mask, maskImage: mask, mixBlendMode: "screen",
  };
  const bg = (SHEATH_BG[p.tex] || SHEATH_BG.bands)(g);
  // Loop offsets are WHOLE TILES, or the pattern jumps once a cycle:
  // with background-size S%, one tile is S / (100 − S) of position.
  //   100% × 50%  → 2 tiles vertical = −200%        (smoke, bands)
  //   140% × 70%  → 1 tile each way  = −350%, −233.3%  (caustic)
  const anim = p.tex === "caustic" ? "gaDriftC" : p.tex === "smoke" ? "gaDrift" : p.flow === "down" ? "gaBandDown" : "gaBandUp";
  const layer = (dir, dur, op, k) => (
    <span key={k} style={{
      ...common, opacity: op, backgroundImage: bg, backgroundSize: p.tex === "caustic" ? "140% 70%" : "100% 50%",
      animation: `${dir} ${dur}s linear infinite`,
    }} />
  );
  if (p.flow === "counter") {
    // Quicksilver: two bands, one up and one down, NEVER in step
    return <>{layer("gaBandUp", p.speed, p.op, "a")}{layer("gaBandDown", p.speed * 1.37, p.op * 0.7, "b")}</>;
  }
  return layer(anim, p.speed, p.op, "a");
}

// molten tongues licking up the rim — the DBZ gold
function Flame2D({ g, p, seed }) {
  const rng = mulberry32(seed + 7);
  return <>{Array.from({ length: p.n }, (_, i) => {
    const x = 50 + (i / (p.n - 1) - 0.5) * 58 + (rng() - 0.5) * 4;
    const edge = Math.abs(i / (p.n - 1) - 0.5) * 2;           // 0 centre, 1 rim
    const h = 46 + (1 - edge) * 30 + rng() * 10;
    const dur = p.speed * (0.8 + rng() * 0.6);
    return (
      <span key={i} style={{
        ...abs, left: `${x}%`, bottom: `${100 - FEET - 2}%`, width: `${14 + rng() * 6}%`, height: `${h}%`,
        borderRadius: "50% 50% 35% 35% / 70% 70% 30% 30%",
        background: `linear-gradient(0deg, ${g.color3}00, ${g.color3}aa 12%, ${g.color} 45%, ${g.color2} 80%, transparent)`,
        filter: "blur(1.5px)", mixBlendMode: "screen", transformOrigin: "50% 100%",
        "--ga-hi": p.op, opacity: 0,
        animation: `gaFlame ${dur}s ease-out ${-rng() * dur}s infinite`,
      }} />
    );
  })}</>;
}

// three rings R/G/B that split apart and recombine into white
function Prism2D({ g, p }) {
  const cols = ["#ff3355", "#33ff88", "#3388ff"];
  return <>{cols.map((c, i) => (
    <span key={c} style={{
      ...abs, left: "50%", top: `${yPct(0.5)}%`, width: "58%", height: "82%", borderRadius: "50%",
      border: `2px solid ${c}`, boxShadow: `0 0 8px ${c}, inset 0 0 8px ${c}`,
      mixBlendMode: "screen", opacity: p.op,
      "--ga-off": `${(i - 1) * p.amp * 30 + (i === 1 ? 2 : 0)}cqw`,
      transform: "translate(-50%,-50%)",
      animation: `gaPrism ${p.speeds[i]}s ease-in-out infinite`,
    }} />
  ))}</>;
}

// a throat of smoke turning at the feet
function Vortex2D({ g, p }) {
  return <span style={{
    ...abs, left: "50%", top: `${FEET}%`, width: "82%", height: "26%",
    transform: "translate(-50%,-50%)", borderRadius: "50%", overflow: "hidden", opacity: p.op,
  }}>
    <span style={{
      ...abs, left: "50%", top: "50%", width: "100%", aspectRatio: "1", marginLeft: "-50%", marginTop: "-50%",
      background: `conic-gradient(from 0deg, ${g.color3}, ${g.color} 12%, transparent 22%, ${g.color2}aa 30%, ${g.color3} 42%, ${g.color} 55%, transparent 66%, ${g.color2}88 76%, ${g.color3} 90%, ${g.color3})`,
      WebkitMaskImage: "radial-gradient(circle, #000 0 8%, transparent 14% 18%, #000 30%, transparent 70%)",
      maskImage: "radial-gradient(circle, #000 0 8%, transparent 14% 18%, #000 30%, transparent 70%)",
      animation: `gaSpin ${p.speed}s linear infinite`, filter: "blur(1px)",
    }} />
  </span>;
}

// a fissure tearing open beside the body, flaring white, sealing
function Fissure2D({ g, p }) {
  return <span style={{
    ...abs, left: "73%", top: `${yPct(0.55)}%`, width: "7%", height: "70%",
    background: `linear-gradient(90deg, transparent, ${g.color} 30%, ${g.color2} 50%, ${g.color} 70%, transparent)`,
    clipPath: "polygon(50% 0, 70% 18%, 40% 36%, 75% 56%, 45% 78%, 50% 100%, 30% 76%, 58% 55%, 22% 35%, 55% 17%)",
    boxShadow: `0 0 12px ${g.color}`, mixBlendMode: "screen", opacity: 0,
    transform: "translate(-50%,-50%)",
    animation: `gaRend ${p.period}s ease-in-out infinite`,
  }} />;
}

// things wheeling round the waist. Half the circle draws behind the
// body, half in front — two layers of the same wheel, masked by phase.
const ORBIT_GLYPH = {
  diamond: (c, s) => ({ width: `${6 * s}cqw`, height: `${6 * s}cqw`, background: `linear-gradient(135deg, #fff, ${c} 45%, ${c}88)`, boxShadow: `0 0 4cqw ${c}` }),
  rune:    (c, s) => ({ width: `${7 * s}cqw`, height: `${7 * s}cqw`, color: c, fontSize: `${8 * s}cqw`, lineHeight: `${7 * s}cqw`, textAlign: "center", textShadow: `0 0 4cqw ${c}`, fontWeight: 900 }),
};
const RUNES = ["ᚱ", "ᛟ", "ᛉ", "ᚦ", "ᛗ", "ᛞ"];
function Orbit2D({ g, p, front }) {
  const c = pick(g, p.c);
  const dur = Math.abs(p.speed);
  const dirn = p.speed < 0 ? "reverse" : "normal";
  const halfAnim = front ? "gaFrontHalf" : "gaBackHalf";
  return <span style={{
    ...abs, left: "50%", top: `${yPct(p.y)}%`, width: 0, height: 0,
    transform: `scaleY(${1 / 2.6})`,
  }}>
    {Array.from({ length: p.n }, (_, i) => {
      // Phase 0 is the right-hand side; the first half-turn (clockwise on
      // screen) is the near side, so that half draws in FRONT.
      const delay = -(i / p.n) * dur;
      const glyph = (ORBIT_GLYPH[p.shape] || ORBIT_GLYPH.diamond)(c, p.size || 1);
      return (
        <span key={i} style={{
          ...abs, left: 0, top: 0, width: 0, height: 0,
          "--ga-r": `${p.r * SPAN}cqw`,
          animation: `gaOrbit ${dur}s linear ${delay}s infinite ${dirn}, ${halfAnim} ${dur}s step-end ${delay}s infinite ${dirn}`,
        }}>
          <span style={{ ...abs, left: 0, top: 0, ...glyph,
            transform: `translate(-50%,-50%)${p.shape === "rune" ? " rotate(-45deg)" : ""}` }}>
            {p.shape === "rune" ? RUNES[i % RUNES.length] : null}
          </span>
        </span>
      );
    })}
  </span>;
}

// a halo above the head
function Halo2D({ g, p }) {
  const c = pick(g, p.c);
  return <span style={{
    ...abs, left: "50%", top: `${yPct(p.y)}%`, width: `${p.r * 200}%`, height: `${p.r * 60}%`,
    borderRadius: "50%", border: `${Math.max(1.2, p.w * 34)}cqw solid ${c}`,
    boxShadow: `0 0 8px ${c}, inset 0 0 6px ${c}`, opacity: p.op,
    transform: "translate(-50%,-50%)", animation: "gaBob 3.2s ease-in-out infinite",
  }} />;
}

// her grin, up in the air — it appears, holds, winks, and is gone
function Grin2D({ g, p }) {
  return <span style={{
    ...abs, left: "50%", top: `${yPct(p.y)}%`, width: "34%", height: "14%",
    opacity: 0, transform: "translate(-50%,-50%)",
    animation: `gaGrin ${p.period}s ease-in-out infinite`,
    filter: `drop-shadow(0 0 4px ${g.color})`,
  }}>
    <svg viewBox="0 0 60 24" width="100%" height="100%" style={{ display: "block", overflow: "visible" }}>
      <path d="M4 6 Q30 30 56 6" fill="none" stroke={g.color} strokeWidth="3.4" strokeLinecap="round" />
      <path d="M12 9 L15 14 M22 12 L24 18 M30 13 L30 19 M38 12 L36 18 M48 9 L45 14" stroke={g.color2} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M16 -4 Q20 -8 24 -4" fill="none" stroke={g.color} strokeWidth="2.4" strokeLinecap="round" />
      <path d="M36 -4 Q40 -6 44 -4" fill="none" stroke={g.color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  </span>;
}

// Sir H'osh Vrown's plate: white-gold catches, sears to brown
function Griddle2D({ g, p }) {
  return <span style={{
    ...abs, left: "50%", top: `${FEET}%`, width: "66%", height: "15%", borderRadius: "50%",
    transform: "translate(-50%,-50%)", opacity: p.op,
    "--ga-a": g.color2, "--ga-b": g.color, "--ga-c": g.color3,
    backgroundImage: `repeating-linear-gradient(35deg, transparent 0 9%, rgba(0,0,0,.25) 10% 12%)`,
    boxShadow: `0 0 10px ${g.color}88`,
    animation: `gaGriddle ${p.period}s ease-in-out infinite`,
    WebkitMaskImage: "radial-gradient(ellipse, #000 55%, transparent 72%)",
    maskImage: "radial-gradient(ellipse, #000 55%, transparent 72%)",
  }} />;
}

// four plates close on the body and clench
function Plates2D({ g, p }) {
  const r = 0.44 * SPAN;
  return <span style={{ ...abs, left: "50%", top: `${yPct(0.48)}%`, width: 0, height: 0 }}>
    {Array.from({ length: p.n }, (_, i) => (
      <span key={i} style={{
        ...abs, left: 0, top: 0, width: 0, height: 0,
        "--ga-a0": `${(360 * i) / p.n + 45}deg`, "--ga-far": `${r}cqw`, "--ga-near": `${r * 0.62}cqw`,
        animation: `gaGrip ${p.period}s cubic-bezier(.7,0,.3,1) infinite`,
      }}>
        <span style={{
          ...abs, left: 0, top: 0, width: "13cqw", height: "7cqw", opacity: p.op,
          transform: "translate(-50%,-50%)",
          background: `linear-gradient(180deg, ${g.color2}, ${g.color} 60%, ${g.color3})`,
          clipPath: "polygon(0 0, 100% 0, 100% 40%, 50% 100%, 0 40%)",
          filter: `drop-shadow(0 0 2cqw ${g.color})`,
        }} />
      </span>
    ))}
  </span>;
}

// the Edict's seal stamping the ground
function Stamp2D({ g, p }) {
  return <span style={{
    ...abs, left: "50%", top: `${FEET}%`, width: "72%", height: "18%", borderRadius: "50%",
    border: `2px double ${g.color}`, boxShadow: `0 0 8px ${g.color}, inset 0 0 10px ${g.color}66`,
    background: `repeating-conic-gradient(from 0deg, ${g.color}55 0 4deg, transparent 4deg 30deg)`,
    "--ga-hi": p.op, opacity: 0, transform: "translate(-50%,-50%)",
    animation: `gaStamp ${p.period}s ease-out infinite`,
  }} />;
}

// a crown of standing water above the head
function Crown2D({ g, p }) {
  return <span style={{
    ...abs, left: "50%", top: `${yPct(p.y)}%`, width: "40%", height: "16%", opacity: p.op,
    transform: "translate(-50%,-50%)", animation: "gaBob 4s ease-in-out infinite",
    filter: `drop-shadow(0 0 4px ${g.color})`,
  }}>
    <svg viewBox="0 0 60 24" width="100%" height="100%" style={{ display: "block", overflow: "visible" }}>
      <ellipse cx="30" cy="18" rx="26" ry="5" fill="none" stroke={g.color} strokeWidth="2.4" />
      <path d="M6 17 L10 4 L18 14 L24 1 L30 13 L36 1 L42 14 L50 4 L54 17" fill={`${g.color}44`} stroke={g.color2} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  </span>;
}

// a broken halo whose shards pull apart, bleed, and snap back
function Cracked2D({ g, p }) {
  const n = 5;
  return <span style={{ ...abs, left: "50%", top: `${yPct(p.y)}%`, width: 0, height: 0, transform: "scaleY(.34)" }}>
    {Array.from({ length: n }, (_, i) => (
      <span key={i} style={{
        ...abs, left: 0, top: 0, width: 0, height: 0,
        "--ga-a0": `${(360 * i) / n}deg`, "--ga-r": `${0.24 * SPAN}cqw`, "--ga-gap": `${4 + (i % 2) * 3}cqw`,
        animation: `gaShard ${p.period}s ease-in-out ${-i * 0.13}s infinite`,
      }}>
        <span style={{
          ...abs, left: 0, top: 0, width: "22cqw", height: "7cqw", opacity: p.op, transform: "translate(-50%,-50%)",
          background: `linear-gradient(90deg, ${g.color3}, ${g.color} 40%, ${g.color2} 60%, ${g.color})`,
          clipPath: "polygon(0 30%, 18% 0, 100% 20%, 86% 100%, 10% 80%)",
          boxShadow: `0 0 4cqw ${g.color}`,
        }} />
        <span style={{
          ...abs, left: 0, top: 0, width: "26cqw", height: "10cqw", borderRadius: "4cqw", transform: "translate(-50%,-50%)",
          background: "#8b3dff", filter: "blur(2px)", opacity: 0, mixBlendMode: "screen",
          animation: `gaBleed ${p.period}s ease-in-out ${-i * 0.13}s infinite`,
        }} />
      </span>
    ))}
  </span>;
}

const LAYERS_2D = {
  motes: Motes2D, floor: Floor2D, sheath: Sheath2D, flame: Flame2D, prism: Prism2D,
  vortex: Vortex2D, fissure: Fissure2D, orbit: Orbit2D, halo: Halo2D, grin: Grin2D,
  griddle: Griddle2D, plates: Plates2D, stamp: Stamp2D, crown: Crown2D, cracked: Cracked2D,
};

/**
 * <GraceAura2D graceId layer="back"|"front" />
 * Fills its positioned parent. `scale` shrinks the whole picture toward
 * the feet for a cramped slot (1 = the full recipe). `still` freezes it on
 * a seeded frame — a grid of forty should animate only the tile in hand.
 */
export function GraceAura2D({ graceId, layer = "back", intensity = 1, scale = 1, still = false, style }) {
  useEffect(ensureKeys, []);
  const rec = useMemo(() => recipeOf(graceId), [graceId]);
  if (!rec) return null;
  const front = layer === "front";
  const seed = hash(rec.grace.id);
  const parts = [];
  rec.layers.forEach(([type, p], i) => {
    const Comp = LAYERS_2D[type];
    if (!Comp) return;
    if (type === "orbit") {                 // an orbit draws in BOTH layers, split by phase
      parts.push(<Comp key={i} g={rec.grace} p={p} seed={seed + i} front={front} />);
      return;
    }
    if (!!FRONT_2D[type] !== front) return;
    parts.push(<Comp key={i} g={rec.grace} p={p} seed={seed + i} />);
  });
  if (!parts.length) return null;
  return (
    <div aria-hidden="true" data-grace={rec.grace.id} className={still ? "ga-still" : undefined} style={{
      ...abs, inset: 0, containerType: "size", opacity: Math.max(0, Math.min(1, intensity)),
      transform: scale !== 1 ? `scale(${scale})` : undefined, transformOrigin: `50% ${FEET}%`,
      ...style,
    }}>{parts}</div>
  );
}

// ════════════════════════════════════════════════════════════════════
// 3D — react-three-fiber. The group's origin is the body's FEET.
// ════════════════════════════════════════════════════════════════════
const TEX = new Map();
function graceTex(key, w, h, draw, repeat = false) {
  if (TEX.has(key)) return TEX.get(key);
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  if (repeat) { t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.RepeatWrapping; }
  TEX.set(key, t);
  return t;
}
// White shapes — tinted by material.color, so one texture serves every Grace.
const softDot = (x, w, h) => { const g = x.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2); g.addColorStop(0, "#fff"); g.addColorStop(0.35, "rgba(255,255,255,.85)"); g.addColorStop(1, "rgba(255,255,255,0)"); x.fillStyle = g; x.fillRect(0, 0, w, h); };
const SHAPE_DRAW = {
  dot: softDot,
  ember: (x, w, h) => { softDot(x, w, h); x.fillStyle = "#fff"; x.beginPath(); x.ellipse(w / 2, h / 2, w * 0.12, h * 0.2, 0, 0, 7); x.fill(); },
  star: (x, w, h) => { softDot(x, w, h); x.fillStyle = "#fff"; x.beginPath(); for (let i = 0; i < 8; i++) { const a = (i * Math.PI) / 4, r = i % 2 ? w * 0.1 : w * 0.46; x.lineTo(w / 2 + Math.cos(a) * r, h / 2 + Math.sin(a) * r); } x.fill(); },
  bubble: (x, w, h) => { x.strokeStyle = "#fff"; x.lineWidth = w * 0.07; x.beginPath(); x.arc(w / 2, h / 2, w * 0.38, 0, 7); x.stroke(); x.fillStyle = "rgba(255,255,255,.8)"; x.beginPath(); x.arc(w * 0.38, h * 0.38, w * 0.09, 0, 7); x.fill(); },
  shred: (x, w, h) => { x.strokeStyle = "#fff"; x.lineCap = "round"; x.lineWidth = w * 0.12; x.beginPath(); x.moveTo(w * 0.3, h * 0.8); x.lineTo(w * 0.7, h * 0.2); x.stroke(); },
  leaf: (x, w, h) => { x.fillStyle = "#fff"; x.beginPath(); x.moveTo(w * 0.1, h * 0.5); x.quadraticCurveTo(w * 0.5, h * 0.05, w * 0.9, h * 0.5); x.quadraticCurveTo(w * 0.5, h * 0.95, w * 0.1, h * 0.5); x.fill(); x.strokeStyle = "rgba(0,0,0,.35)"; x.lineWidth = w * 0.04; x.beginPath(); x.moveTo(w * 0.12, h * 0.5); x.lineTo(w * 0.88, h * 0.5); x.stroke(); },
  drop: (x, w, h) => { x.fillStyle = "#fff"; x.beginPath(); x.moveTo(w / 2, h * 0.1); x.quadraticCurveTo(w * 0.85, h * 0.62, w / 2, h * 0.9); x.quadraticCurveTo(w * 0.15, h * 0.62, w / 2, h * 0.1); x.fill(); },
  ash: (x, w, h) => { x.fillStyle = "#fff"; x.beginPath(); x.moveTo(w * 0.2, h * 0.1); x.lineTo(w * 0.9, h * 0.35); x.lineTo(w * 0.7, h * 0.9); x.lineTo(w * 0.1, h * 0.7); x.fill(); },
  diamond: (x, w, h) => { const g = x.createLinearGradient(0, 0, w, h); g.addColorStop(0, "#fff"); g.addColorStop(1, "rgba(255,255,255,.55)"); x.fillStyle = g; x.beginPath(); x.moveTo(w / 2, h * 0.05); x.lineTo(w * 0.8, h / 2); x.lineTo(w / 2, h * 0.95); x.lineTo(w * 0.2, h / 2); x.fill(); },
  flame: (x, w, h) => { const g = x.createLinearGradient(0, h, 0, 0); g.addColorStop(0, "rgba(255,255,255,0)"); g.addColorStop(0.15, "rgba(255,255,255,.9)"); g.addColorStop(0.6, "rgba(255,255,255,.6)"); g.addColorStop(1, "rgba(255,255,255,0)"); x.fillStyle = g; x.beginPath(); x.moveTo(w / 2, 0); x.bezierCurveTo(w * 0.95, h * 0.45, w * 0.9, h, w / 2, h); x.bezierCurveTo(w * 0.1, h, w * 0.05, h * 0.45, w / 2, 0); x.fill(); },
  fissure: (x, w, h) => { x.fillStyle = "#fff"; x.shadowColor = "#fff"; x.shadowBlur = 6; x.beginPath(); const pts = [[0.5, 0], [0.7, 0.18], [0.4, 0.36], [0.75, 0.56], [0.45, 0.78], [0.5, 1], [0.3, 0.76], [0.58, 0.55], [0.22, 0.35], [0.55, 0.17]]; pts.forEach(([a, b]) => x.lineTo(a * w, b * h)); x.fill(); },
  grin: (x, w, h) => { x.strokeStyle = "#fff"; x.lineCap = "round"; x.lineWidth = h * 0.12; x.beginPath(); x.moveTo(w * 0.07, h * 0.45); x.quadraticCurveTo(w * 0.5, h * 1.25, w * 0.93, h * 0.45); x.stroke(); x.lineWidth = h * 0.08; [[0.28, 0.12], [0.72, 0.12]].forEach(([a, b]) => { x.beginPath(); x.arc(a * w, b * h + h * 0.12, w * 0.07, Math.PI * 1.1, Math.PI * 1.9); x.stroke(); }); },
};
RUNES.forEach((r, i) => {
  SHAPE_DRAW["rune" + i] = (x, w, h) => { x.globalAlpha = 0.35; softDot(x, w, h); x.globalAlpha = 1; x.fillStyle = "#fff"; x.shadowColor = "#fff"; x.shadowBlur = 4; x.font = `900 ${Math.round(h * 0.82)}px "Segoe UI Symbol", "Noto Sans Runic", serif`; x.textAlign = "center"; x.textBaseline = "middle"; x.fillText(r, w / 2, h / 2 + h * 0.04); };
});
const shapeTex = (shape) => graceTex("ga-shape-" + shape, 64, shape === "flame" || shape === "fissure" ? 128 : 64, SHAPE_DRAW[shape] || softDot);

// sheath textures — white, repeating, tinted by material colour
const SHEATH_DRAW = {
  bands: (x, w, h) => { for (let i = 0; i < 2; i++) { const y = (i + 0.5) * h / 2; const g = x.createLinearGradient(0, y - h * 0.05, 0, y + h * 0.05); g.addColorStop(0, "rgba(255,255,255,0)"); g.addColorStop(0.5, "rgba(255,255,255,1)"); g.addColorStop(1, "rgba(255,255,255,0)"); x.fillStyle = g; x.fillRect(0, y - h * 0.05, w, h * 0.1); } },
  ripple: (x, w, h) => { for (let i = 0; i < 4; i++) { x.fillStyle = "rgba(255,255,255,.8)"; x.fillRect(0, (i + 0.5) * h / 4, w, h * 0.025); } },
  smoke: (x, w, h) => { const r = mulberry32(99); for (let i = 0; i < 26; i++) { const cx = r() * w, cy = r() * h, rad = 6 + r() * 16; const g = x.createRadialGradient(cx, cy, 0, cx, cy, rad); g.addColorStop(0, "rgba(255,255,255,.7)"); g.addColorStop(1, "rgba(255,255,255,0)"); x.fillStyle = g; x.fillRect(cx - rad, cy - rad, rad * 2, rad * 2); } },
  caustic: (x, w, h) => { const r = mulberry32(7); x.strokeStyle = "rgba(255,255,255,.9)"; x.lineWidth = 1.6; for (let i = 0; i < 18; i++) { x.beginPath(); let px = r() * w, py = r() * h; x.moveTo(px, py); for (let k = 0; k < 4; k++) { px += (r() - 0.5) * 30; py += (r() - 0.5) * 30; x.lineTo(px, py); } x.stroke(); } },
};
const fadeTex = () => graceTex("ga-fade", 4, 64, (x, w, h) => { const g = x.createLinearGradient(0, 0, 0, h); g.addColorStop(0, "#000"); g.addColorStop(0.25, "#fff"); g.addColorStop(0.7, "#fff"); g.addColorStop(1, "#000"); x.fillStyle = g; x.fillRect(0, 0, w, h); });
const ringFadeTex = () => graceTex("ga-ringfade", 64, 64, (x, w, h) => { const g = x.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2); g.addColorStop(0, "#000"); g.addColorStop(0.55, "#000"); g.addColorStop(0.8, "#fff"); g.addColorStop(1, "#000"); x.fillStyle = g; x.fillRect(0, 0, w, h); });
const spiralTex = () => graceTex("ga-spiral", 128, 128, (x, w, h) => { x.translate(w / 2, h / 2); for (let arm = 0; arm < 3; arm++) { x.beginPath(); for (let t = 0; t < 1; t += 0.01) { const a = arm * 2.094 + t * 7; const r = t * w * 0.48; x.lineTo(Math.cos(a) * r, Math.sin(a) * r); } x.strokeStyle = "rgba(255,255,255,.9)"; x.lineWidth = 7; x.shadowColor = "#fff"; x.shadowBlur = 8; x.stroke(); } });
const griddleTex = () => graceTex("ga-griddle", 128, 128, (x, w, h) => { x.fillStyle = "#fff"; x.beginPath(); x.arc(w / 2, h / 2, w * 0.48, 0, 7); x.fill(); x.strokeStyle = "rgba(0,0,0,.28)"; x.lineWidth = 3; for (let i = -6; i < 7; i++) { x.beginPath(); x.moveTo(w / 2 + i * 10 - 40, 0); x.lineTo(w / 2 + i * 10 + 40, h); x.stroke(); } });
const stampTex = () => graceTex("ga-stamp", 128, 128, (x, w, h) => { x.translate(w / 2, h / 2); x.strokeStyle = "#fff"; x.lineWidth = 4; x.beginPath(); x.arc(0, 0, w * 0.46, 0, 7); x.stroke(); x.lineWidth = 2; x.beginPath(); x.arc(0, 0, w * 0.38, 0, 7); x.stroke(); for (let i = 0; i < 12; i++) { x.rotate(Math.PI / 6); x.fillStyle = "#fff"; x.fillRect(-1.5, -w * 0.46, 3, w * 0.08); } x.font = `900 ${w * 0.3}px serif`; x.textAlign = "center"; x.textBaseline = "middle"; x.fillText("⚜", 0, 2); });

const addMat = (color, opacity, extra = {}) => new THREE.MeshBasicMaterial({
  color, transparent: true, opacity, depthWrite: false, toneMapped: false,
  blending: THREE.AdditiveBlending, side: THREE.DoubleSide, ...extra,
});
const spriteMat = (tex, color, opacity) => new THREE.SpriteMaterial({
  map: tex, color, transparent: true, opacity, depthWrite: false, toneMapped: false,
  blending: THREE.AdditiveBlending,
});
function useDisposable(make, deps) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const v = useMemo(make, deps);
  useEffect(() => () => { (Array.isArray(v) ? v : [v]).forEach((m) => m && m.dispose && m.dispose()); }, [v]);
  return v;
}
const ease = (t) => t * t * (3 - 2 * t);

function Floor3D({ g, p, H }) {
  const c = pick(g, p.c);
  const R0 = 0.42 * H;
  const n = p.pulse ? (p.n || 1) : 1;
  const mats = useDisposable(() => Array.from({ length: n }, () => addMat(c, p.op)), [c, p.op, n]);
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    refs.current.forEach((m, i) => {
      if (!m) return;
      if (p.pulse) {
        const k = ((t / p.period) + i / n) % 1;
        m.scale.setScalar(0.35 + k * 0.9);
        mats[i].opacity = p.op * (k < 0.18 ? k / 0.18 : 1 - (k - 0.18) / 0.82);
      } else if (p.breathe) {
        const k = 0.5 + 0.5 * Math.sin((t / p.breathe) * Math.PI * 2);
        m.scale.setScalar(0.94 + k * 0.1);
        mats[i].opacity = p.op * (0.4 + 0.6 * k);
      }
    });
  });
  const inner = R0 * (1 - Math.max(0.06, p.w * 2.6));
  return <>{mats.map((m, i) => (
    <mesh key={i} ref={(o) => (refs.current[i] = o)} position={[0, 0.012 + i * 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]} material={m} renderOrder={2}>
      <ringGeometry args={[inner, R0, 48]} />
    </mesh>
  ))}</>;
}

function Sheath3D({ g, p, H }) {
  const tex = useMemo(() => {
    const base = graceTex("ga-sheath-" + p.tex, 64, 256, SHEATH_DRAW[p.tex] || SHEATH_DRAW.bands, true);
    return base ? base.clone() : null;            // a clone owns its own offset
  }, [p.tex]);
  const tex2 = useMemo(() => (p.flow === "counter" && tex ? tex.clone() : null), [p.flow, tex]);
  const mats = useDisposable(() => {
    const a = addMat(p.tex === "smoke" ? g.color : p.tex === "bands" ? g.color : g.color2, p.op, { map: tex, alphaMap: fadeTex() });
    const b = tex2 ? addMat(g.color, p.op * 0.7, { map: tex2, alphaMap: fadeTex() }) : null;
    return b ? [a, b] : [a];
  }, [g, p.op, p.tex, tex, tex2]);
  useEffect(() => () => { tex && tex.dispose(); tex2 && tex2.dispose(); }, [tex, tex2]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    const dir = p.flow === "down" ? 1 : -1;
    if (tex) {
      if (tex.repeat.x === 1) tex.repeat.set(p.tex === "caustic" ? 2 : 3, p.tex === "bands" || p.tex === "ripple" ? 1 : 1.5);
      tex.offset.set(p.tex === "caustic" || p.tex === "smoke" ? t / (p.speed * 3) : 0, (dir * t) / p.speed);
    }
    if (tex2) { if (tex2.repeat.x === 1) tex2.repeat.set(3, 1); tex2.offset.set(0.37, t / (p.speed * 1.37)); }
  });
  const geo = [0.3 * H, 0.4 * H, 1.05 * H, 28, 1, true];
  return <>{mats.map((m, i) => (
    <mesh key={i} position={[0, 0.52 * H, 0]} material={m} renderOrder={3}>
      <cylinderGeometry args={i ? [0.33 * H, 0.43 * H, 1.0 * H, 28, 1, true] : geo} />
    </mesh>
  ))}</>;
}

// Sprite particles. One seeded path per mote, evaluated each frame.
function Motes3D({ g, p, H, seed }) {
  const n = p.n;
  const plan = useMemo(() => {
    const rng = mulberry32(seed);
    return Array.from({ length: n }, (_, i) => ({
      a: rng() * Math.PI * 2, r: 0.26 + rng() * 0.18, y0: rng(), dur: p.speed * (0.75 + rng() * 0.5),
      off: rng(), wob: rng() * 6, side: i % 2 ? 1 : -1, rune: i,
    }));
  }, [seed, n, p.speed]);
  const mats = useDisposable(() => plan.map((m, i) => {
    const c = p.rainbow ? RAINBOW[i % RAINBOW.length] : p.mix ? (i % 2 ? g.color2 : g.color) : pick(g, p.c);
    return spriteMat(shapeTex(p.shape), c, 1);
  }), [plan, g, p.shape, p.c, p.rainbow, p.mix]);
  const refs = useRef([]);
  const size = 0.055 * H * (p.size || 1);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    plan.forEach((m, i) => {
      const s = refs.current[i]; if (!s) return;
      const k = ((t / m.dur) + m.off) % 1;
      let x, y, z, op = 1, rot = 0;
      const ax = Math.cos(m.a), az = Math.sin(m.a);
      switch (p.dir) {
        case "up":     x = ax * m.r * H; z = az * m.r * H; y = (m.y0 * 0.3 + k * 0.9) * H; x += Math.sin(t + m.wob) * 0.02 * H; break;
        case "rise":   x = ax * m.r * H * 0.9; z = az * m.r * H * 0.9; y = (0.02 + k * 0.28) * H; rot = 0.6; break;
        case "down":   x = ax * m.r * 0.55 * H; z = az * m.r * 0.55 * H; y = (1.08 - k * 1.06) * H; break;
        case "sink":   x = ax * m.r * H; z = az * m.r * H; y = (1.1 - k * 0.7) * H; rot = t * 2 + m.wob; break;
        case "in":     { const rr = (1 - k) * 0.5 + 0.04; const aa = m.a + k * 2.4; x = Math.cos(aa) * rr * H; z = Math.sin(aa) * rr * H; y = (0.5 - k * 0.46) * H; break; }
        case "spiral": { const aa = m.a + m.side * k * Math.PI * 2.2; const rr = (0.26 + 0.12 * Math.sin(k * 3)) * H; x = Math.cos(aa) * rr; z = Math.sin(aa) * rr; y = k * 1.15 * H; rot = aa * 1.5; break; }
        default:       x = ax * m.r * H; z = az * m.r * H; y = (0.15 + m.y0 * 0.95) * H; break; // still
      }
      if (p.dir === "still" || p.twinkle) op = 0.2 + 0.8 * (0.5 + 0.5 * Math.sin((t + m.wob) * (p.dir === "still" ? 2.2 : 9)));
      if (p.dir !== "still") op *= k < 0.15 ? k / 0.15 : k > 0.8 ? (1 - k) / 0.2 : 1;
      s.position.set(x, y, z);
      mats[i].opacity = op;
      mats[i].rotation = rot;
    });
  });
  return <>{plan.map((m, i) => (
    <sprite key={i} ref={(o) => (refs.current[i] = o)} material={mats[i]} scale={[size, size * (p.shape === "ember" || p.shape === "drop" ? 1.4 : 1), size]} renderOrder={4} />
  ))}</>;
}

function Flame3D({ g, p, H, seed }) {
  const plan = useMemo(() => {
    const rng = mulberry32(seed + 7);
    return Array.from({ length: p.n + 3 }, (_, i) => ({ a: (i / (p.n + 3)) * Math.PI * 2 + rng() * 0.4, dur: p.speed * (0.8 + rng() * 0.6), off: rng(), h: 0.55 + rng() * 0.35 }));
  }, [seed, p.n, p.speed]);
  const mats = useDisposable(() => plan.map((_, i) => spriteMat(shapeTex("flame"), i % 3 === 0 ? g.color2 : i % 3 === 1 ? g.color : g.color3, p.op)), [plan, g, p.op]);
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    plan.forEach((m, i) => {
      const s = refs.current[i]; if (!s) return;
      const k = ((t / m.dur) + m.off) % 1;
      const hh = m.h * H * (0.55 + 0.55 * Math.sin(k * Math.PI));
      const w = 0.2 * H * (1 - k * 0.5);
      s.scale.set(w, hh, 1);
      s.position.set(Math.cos(m.a) * 0.28 * H, hh * 0.5 + k * 0.35 * H, Math.sin(m.a) * 0.28 * H);
      mats[i].opacity = p.op * Math.sin(k * Math.PI);
    });
  });
  return <>{plan.map((m, i) => <sprite key={i} ref={(o) => (refs.current[i] = o)} material={mats[i]} renderOrder={3} />)}</>;
}

function Prism3D({ p, H }) {
  const cols = ["#ff3355", "#33ff88", "#3388ff"];
  const mats = useDisposable(() => cols.map((c) => addMat(c, p.op)), [p.op]);
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    refs.current.forEach((m, i) => {
      if (!m) return;
      const k = Math.sin((t / p.speeds[i]) * Math.PI * 2);
      m.position.y = (0.5 + (i - 1) * p.amp * k + 0.25 * Math.sin(t / (p.speeds[i] * 1.7))) * H;
    });
  });
  return <>{mats.map((m, i) => (
    <mesh key={i} ref={(o) => (refs.current[i] = o)} rotation={[Math.PI / 2, 0, 0]} material={m} renderOrder={3}>
      <torusGeometry args={[0.38 * H, 0.012 * H, 6, 48]} />
    </mesh>
  ))}</>;
}

function Vortex3D({ g, p, H }) {
  const mats = useDisposable(() => [
    addMat(g.color, p.op, { map: spiralTex(), alphaMap: ringFadeTex() }),
    addMat(g.color2, p.op * 0.6, { map: spiralTex() }),
  ], [g, p.op]);
  const a = useRef(), b = useRef();
  useFrame((_, dt) => {
    const w = (Math.PI * 2) / p.speed;
    if (a.current) a.current.rotation.z -= w * Math.min(dt, 0.05);
    if (b.current) b.current.rotation.z += w * 1.6 * Math.min(dt, 0.05);
  });
  return <>
    <mesh ref={a} position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]} material={mats[0]} renderOrder={2}><circleGeometry args={[0.6 * H, 40]} /></mesh>
    <mesh ref={b} position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} material={mats[1]} renderOrder={2}><circleGeometry args={[0.22 * H, 24]} /></mesh>
  </>;
}

function Fissure3D({ g, p, H }) {
  const mat = useDisposable(() => spriteMat(shapeTex("fissure"), g.color, 0), [g]);
  const s = useRef();
  useFrame(({ clock }) => {
    if (!s.current) return;
    const k = (clock.elapsedTime / p.period) % 1;
    // closed … tears open … flares white … seals
    let sx = 0.02, op = 0.22;                     // the seam never fully vanishes
    if (k > 0.55 && k < 0.64) { const u = (k - 0.55) / 0.09; sx = 0.02 + u * 0.1; op = 0.22 + u * 0.78; }
    else if (k >= 0.64 && k < 0.72) { sx = 0.12 + (k - 0.64) * 1.4; op = 1; }
    else if (k >= 0.72 && k < 0.9) { const u = (k - 0.72) / 0.18; sx = 0.23 * (1 - u) + 0.02; op = 1 - u * 0.78; }
    mat.color.set(k >= 0.64 && k < 0.72 ? g.color2 : g.color);
    mat.opacity = op * p.op;
    s.current.scale.set(sx * H, 0.75 * H, 1);
  });
  return <sprite ref={s} position={[0.42 * H, 0.55 * H, 0]} material={mat} renderOrder={5} />;
}

function Orbit3D({ g, p, H, seed }) {
  const c = pick(g, p.c);
  const mats = useDisposable(() => Array.from({ length: p.n }, (_, i) =>
    spriteMat(shapeTex(p.shape === "rune" ? "rune" + (i % RUNES.length) : "diamond"), c, 1)), [c, p.n, p.shape]);
  const grp = useRef();
  // NEGATIVE: the 2D wheel turns clockwise on screen (right → near →
  // left); seen from above that is clockwise too, and three's +Y turns
  // the other way. Same recipe, same direction, both pictures.
  useFrame((_, dt) => { if (grp.current) grp.current.rotation.y -= ((Math.PI * 2) / p.speed) * Math.min(dt, 0.05); });
  const sz = (p.shape === "rune" ? 0.12 : 0.08) * H * (p.size || 1);
  return <group ref={grp} position={[0, p.y * H, 0]}>
    {mats.map((m, i) => {
      const a = (i / p.n) * Math.PI * 2;
      return <sprite key={i} material={m} position={[Math.cos(a) * p.r * H, 0, Math.sin(a) * p.r * H]} scale={[sz, sz, sz]} renderOrder={4} />;
    })}
  </group>;
}

function Halo3D({ g, p, H }) {
  const mat = useDisposable(() => addMat(pick(g, p.c), p.op), [g, p.c, p.op]);
  const m = useRef();
  useFrame(({ clock }) => { if (m.current) m.current.position.y = (p.y + 0.02 * Math.sin(clock.elapsedTime * 2)) * H; });
  return <mesh ref={m} rotation={[Math.PI / 2, 0, 0]} material={mat} renderOrder={4}>
    <torusGeometry args={[p.r * H, p.w * 0.5 * H, 8, 40]} />
  </mesh>;
}

function Grin3D({ g, p, H }) {
  const mat = useDisposable(() => spriteMat(shapeTex("grin"), g.color, 0), [g]);
  const s = useRef();
  useFrame(({ clock }) => {
    if (!s.current) return;
    const k = (clock.elapsedTime / p.period) % 1;
    let op = 0, sy = 1;
    if (k > 0.62 && k < 0.7) op = (k - 0.62) / 0.08;
    else if (k >= 0.7 && k < 0.9) { op = 1; if (k > 0.82 && k < 0.88) sy = 0.3; }
    else if (k >= 0.9 && k < 0.95) op = 1 - (k - 0.9) / 0.05;
    mat.opacity = op * 0.95;
    s.current.scale.set(0.34 * H, 0.14 * H * sy, 1);
  });
  return <sprite ref={s} position={[0, p.y * H, 0]} material={mat} renderOrder={5} />;
}

function Griddle3D({ g, p, H }) {
  const mat = useDisposable(() => new THREE.MeshBasicMaterial({ map: griddleTex(), alphaMap: null, color: g.color2, transparent: true, opacity: p.op, depthWrite: false, toneMapped: false }), [g, p.op]);
  const A = useMemo(() => [new THREE.Color(g.color2), new THREE.Color(g.color), new THREE.Color(g.color3)], [g]);
  useFrame(({ clock }) => {
    const k = (clock.elapsedTime / p.period) % 1;
    if (k < 0.45) mat.color.copy(A[0]).lerp(A[1], ease(k / 0.45));
    else if (k < 0.7) mat.color.copy(A[1]).lerp(A[2], ease((k - 0.45) / 0.25));
    else mat.color.copy(A[2]).lerp(A[0], ease((k - 0.7) / 0.3));
  });
  return <mesh position={[0, 0.011, 0]} rotation={[-Math.PI / 2, 0, 0]} material={mat} renderOrder={2}><circleGeometry args={[0.4 * H, 40]} /></mesh>;
}

function Plates3D({ g, p, H }) {
  // Four curved plates of dark steel, each with a lit rim, closing on the
  // body and clenching — the Grip, the only Grace that CONTRACTS.
  const mats = useDisposable(() => [
    new THREE.MeshBasicMaterial({ color: g.color3, transparent: true, opacity: p.op * 0.55, depthWrite: false, toneMapped: false, side: THREE.DoubleSide }),
    addMat(g.color2, p.op),
  ], [g, p.op]);
  const refs = useRef([]);
  useFrame(({ clock }) => {
    const k = (clock.elapsedTime / p.period) % 1;
    const u = k < 0.4 ? ease(k / 0.4) : k < 0.6 ? 1 : 1 - ease((k - 0.6) / 0.4);
    const push = (0.16 - 0.16 * u) * H;
    refs.current.forEach((m, i) => {
      if (!m) return;
      const a = (i / p.n) * Math.PI * 2 + Math.PI / 4;
      m.position.set(Math.cos(a) * push, 0, Math.sin(a) * push);
    });
  });
  const R = 0.36 * H, arc = 0.9, h = 0.34 * H;
  return <>{Array.from({ length: p.n }, (_, i) => {
    // cylinder theta 0 points +Z (x = sinθ, z = cosθ), so an XZ angle t is
    // θ = π/2 − t. A torus arc, laid flat by Rx(π/2), starts at t = its Z-roll.
    const a = (i / p.n) * Math.PI * 2 + Math.PI / 4;
    const start = Math.PI / 2 - a - arc / 2;
    return (
      <group key={i} ref={(o) => (refs.current[i] = o)}>
        <mesh material={mats[0]} position={[0, 0.46 * H, 0]} renderOrder={3}>
          <cylinderGeometry args={[R, R * 1.06, h, 10, 1, true, start, arc]} />
        </mesh>
        {[-1, 1].map((e) => (
          <mesh key={e} material={mats[1]} position={[0, 0.46 * H + (e * h) / 2, 0]} rotation={[Math.PI / 2, 0, a - arc / 2]} renderOrder={4}>
            <torusGeometry args={[e > 0 ? R : R * 1.06, 0.008 * H, 4, 10, arc]} />
          </mesh>
        ))}
      </group>
    );
  })}</>;
}

function Stamp3D({ g, p, H }) {
  const mat = useDisposable(() => addMat(g.color, 0, { map: stampTex() }), [g]);
  const m = useRef();
  useFrame(({ clock }) => {
    if (!m.current) return;
    const k = (clock.elapsedTime / p.period) % 1;
    let s = 1.6, op = 0;
    if (k > 0.7 && k < 0.78) { const u = (k - 0.7) / 0.08; s = 1.6 - 0.6 * ease(u); op = u; }
    else if (k >= 0.78) { s = 1 + (k - 0.78) * 0.1; op = 1 - (k - 0.78) / 0.22; }
    m.current.scale.setScalar(s);
    mat.opacity = op * p.op;
  });
  return <mesh ref={m} position={[0, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]} material={mat} renderOrder={2}><planeGeometry args={[0.9 * H, 0.9 * H]} /></mesh>;
}

function Crown3D({ g, p, H }) {
  const mats = useDisposable(() => [addMat(g.color, p.op), addMat(g.color2, p.op * 0.9)], [g, p.op]);
  const grp = useRef();
  useFrame(({ clock }) => {
    if (!grp.current) return;
    const t = clock.elapsedTime;
    grp.current.position.y = (p.y + 0.025 * Math.sin(t * 1.6)) * H;
    grp.current.rotation.y = t * 0.35;
  });
  const r = 0.17 * H, spikes = 7;
  return <group ref={grp}>
    <mesh rotation={[Math.PI / 2, 0, 0]} material={mats[0]} renderOrder={4}><torusGeometry args={[r, 0.013 * H, 6, 36]} /></mesh>
    {Array.from({ length: spikes }, (_, i) => {
      const a = (i / spikes) * Math.PI * 2;
      return <mesh key={i} position={[Math.cos(a) * r, (i % 2 ? 0.045 : 0.065) * H, Math.sin(a) * r]} material={mats[1]} renderOrder={4}>
        <coneGeometry args={[0.022 * H, (i % 2 ? 0.09 : 0.13) * H, 5]} />
      </mesh>;
    })}
  </group>;
}

function Cracked3D({ g, p, H }) {
  const n = 5;
  const mats = useDisposable(() => [addMat(g.color, p.op), addMat("#8b3dff", 0)], [g, p.op]);
  const refs = useRef([]);
  const arc = ((Math.PI * 2) / n) * 0.78;
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    let bleed = 0;
    refs.current.forEach((m, i) => {
      if (!m) return;
      const k = ((t - i * 0.13) / p.period) % 1;
      const u = k < 0.45 ? ease(k / 0.45) : k < 0.6 ? 1 : 1 - ease((k - 0.6) / 0.4);
      const a = (i / n) * Math.PI * 2;
      const push = u * (0.04 + (i % 2) * 0.025) * H;
      m.position.set(Math.cos(a + arc / 2) * push, p.y * H + u * 0.01 * H * (i % 2 ? 1 : -1), Math.sin(a + arc / 2) * push);
      if (i === 0) bleed = k > 0.4 && k < 0.62 ? 1 - Math.abs(k - 0.51) / 0.11 : 0;
    });
    mats[1].opacity = Math.max(0, bleed) * 0.7;
  });
  return <>{Array.from({ length: n }, (_, i) => (
    <group key={i} ref={(o) => (refs.current[i] = o)}>
      <mesh rotation={[Math.PI / 2, 0, (i / n) * Math.PI * 2]} material={mats[0]} renderOrder={4}>
        <torusGeometry args={[0.2 * H, 0.016 * H, 5, 10, arc]} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, (i / n) * Math.PI * 2]} material={mats[1]} renderOrder={3}>
        <torusGeometry args={[0.2 * H, 0.04 * H, 5, 10, arc]} />
      </mesh>
    </group>
  ))}</>;
}

const LAYERS_3D = {
  motes: Motes3D, floor: Floor3D, sheath: Sheath3D, flame: Flame3D, prism: Prism3D,
  vortex: Vortex3D, fissure: Fissure3D, orbit: Orbit3D, halo: Halo3D, grin: Grin3D,
  griddle: Griddle3D, plates: Plates3D, stamp: Stamp3D, crown: Crown3D, cracked: Cracked3D,
};

/**
 * <GraceAura3D graceId height={1} />
 * A group at the body's feet. `height` is the body's height in world
 * units; the whole Grace scales off it. `intensity` < 1 thins the
 * group for a cramped scene (it is applied as a uniform scale toward
 * the feet, never by dimming a material another layer animates).
 */
export function GraceAura3D({ graceId, height = 1, intensity = 1, ...rest }) {
  const rec = useMemo(() => recipeOf(graceId), [graceId]);
  if (!rec) return null;
  const seed = hash(rec.grace.id);
  const k = Math.max(0.5, Math.min(1, intensity));
  return (
    <group {...rest} scale={[k, k, k]} name={"grace_" + rec.grace.id}>
      {rec.layers.map(([type, p], i) => {
        const Comp = LAYERS_3D[type];
        return Comp ? <Comp key={rec.grace.id + i} g={rec.grace} p={p} H={height} seed={seed + i} /> : null;
      })}
    </group>
  );
}

export default GraceAura2D;
